import { IUser } from '@giphy/js-types'
import Header from 'modules/header/components/header'
import Favorites from 'modules/search/containers/favorites'
import { Suspense, useContext, useEffect, useRef } from 'react'
import { Provider } from 'react-redux'
import { Route, Switch } from 'react-router'
import { BrowserRouter } from 'react-router-dom'
import entryPoint from 'shared/components/entry-point-hoc'
import GDBootstrapper from 'shared/components/gd-bootstrapper'
import GTALogger from 'shared/components/gta-logger'
import ReportOnSPA from 'shared/components/router/report-on-spa'
import ContextManagers from 'shared/context-managers'
import { getDefaultStore } from 'shared/redux/init-store'
import routes from 'shared/routes'
import About from './about/route'
import AppsLandingRoute from './apps-landing/components/route'
import ArtistsRoute from './artists/route'
import Categories from './categories'
import Channel from './channel/components/app'
import CurationRoute from './curation-station/route'
import DashboardPage from './dashboard/dashboard'
import Error404 from './error-pages/404'
import Error403 from './error-pages/403'
import GifDetailRoute from './gif-detail/components/route'
import Homebase from './homebase/route'
import Homepage from './homepage/route'
import Labs from './labs'
import SiteContainer from './site-container'
import ChannelStoriesRoute from './stories/route'
import StoryRoute from './story/components/route'
import TeamPage from './team/team'
import TrendingRoute from './trending/route'
import Apply from './user/apply'
import Join from './user/join'
import Login from './user/auth'
import PasswordChange from './user/password-change'
import PasswordReset from './user/password-reset'
import Settings from './user/settings'
import VideosRoute, { VideoDetailRoute } from './videos/route'
import UIbeta from 'shared/components/beta-env/notice'
import BetaNoticeBanner from 'shared/components/beta-env/banner'
import ClipsUploadBanner from 'shared/components/clips-upload-banner'
import { FlashMessageContext } from '../shared/components/flash-message/flash-message'
import ArSceneLandingPage from 'shared/components/ar-scene-landing-page'
import TransparencyReport from './transparency-reports'

const store = getDefaultStore({})

type Props = {
    id: string | number
    pageData: any
    showSearch?: boolean
    showBrowse?: boolean
    searchTerm?: boolean
    user?: IUser
    contextId?: string
}

const BackendMessages = ({ pageData }) => {
    const { showMessage } = useContext(FlashMessageContext)

    useEffect(() => {
        if (pageData?.flash_msg) {
            showMessage({ slug: pageData.flash_msg })
        }
    }, [pageData])

    return null
}

const App = (props: any) => {
    const { pageData = {}, user, showSearch, showBrowse, searchTerm, contextId }: Props = props

    const is404 = useRef(pageData.status_code === 404)
    const is403 = useRef(pageData.status_code === 403)
    // if we have is404 in the template on first render
    if (is404.current) {
        // then the current pathname is a 404
        routes.ERROR_404.path = location.pathname
    }
    if (is403.current) {
        // then the current pathname is a 404
        routes.ERROR_403.path = location.pathname
    }
    // page load context id for channel pages choosing, root, branch or leaf
    const channelType = useRef(contextId)
    if (channelType.current) {
        // there's no SPA but we're close...
        // we'd handle it with location state with an id when we history.push
        routes.CHANNEL.path = location.pathname
    }

    return (
        <Provider store={store}>
            <SiteContainer>
                <Suspense fallback={null}>
                    <BrowserRouter>
                        <GDBootstrapper initData={pageData}>
                            <ContextManagers>
                                {!pageData.hideHeader && (
                                    <Header
                                        hideLogo={pageData.hideLogo}
                                        {...{ user, showSearch, showBrowse, searchTerm }}
                                    />
                                )}
                                <BackendMessages pageData={pageData} />
                                <GTALogger />
                                <ReportOnSPA />
                                <ClipsUploadBanner />
                                <UIbeta />
                                <BetaNoticeBanner />
                                <Switch>
                                    <Route {...routes.CHANNEL} render={() => <Channel {...props} />} />
                                    <Route {...routes.ERROR_404} exact component={Error404} />
                                    <Route {...routes.ERROR_403} exact component={Error403} />
                                    <Route {...routes.APPLY} component={Apply} />
                                    <Route {...routes.DASHBOARD} component={DashboardPage} />
                                    <Route {...routes.JOIN} component={Join} />
                                    <Route {...routes.ABOUT} exact component={About} />
                                    <Route {...routes.PASSWORD_RESET} component={PasswordReset} />
                                    <Route {...routes.PASSWORD_CHANGE} component={PasswordChange} />
                                    <Route {...routes.LABS} component={Labs} />
                                    <Route {...routes.LOGIN} component={Login} />
                                    <Route {...routes.CHANNEL_STORIES} component={ChannelStoriesRoute} />
                                    <Route {...routes.ARTISTS} component={ArtistsRoute} />
                                    <Route {...routes.STORY} component={StoryRoute} />
                                    <Route {...routes.GIF_DETAIL} component={GifDetailRoute} />
                                    <Route {...routes.CLIPS} component={VideosRoute} />
                                    <Route {...routes.TRENDING_CLIPS} component={VideosRoute} />
                                    <Route {...routes.CLIPS_DETAIL} component={VideoDetailRoute} />
                                    <Route {...routes.TRENDING} component={TrendingRoute} />
                                    <Route {...routes.APPS} component={AppsLandingRoute} />
                                    <Route {...routes.SETTINGS} component={Settings} />
                                    <Route {...routes.HOMEBASE} component={Homebase} />
                                    <Route {...routes.EDIT_GIFS} component={CurationRoute} />
                                    <Route {...routes.TEAM} component={TeamPage} />
                                    <Route {...routes.CATEGORIES} component={Categories} />
                                    <Route {...routes.FAVORITES} component={Favorites} />
                                    <Route {...routes.HOME} component={Homepage} />
                                    <Route {...routes.AR_SCENE_LANDING_PAGE} component={ArSceneLandingPage} />
                                    <Route {...routes.TRANSPARENCY} component={TransparencyReport} />

                                    <Route exact component={Error404} />
                                </Switch>
                            </ContextManagers>
                        </GDBootstrapper>
                    </BrowserRouter>
                </Suspense>
            </SiteContainer>
        </Provider>
    )
}

export default entryPoint(store)(App)
